import Immutable from 'immutable';
import React from 'react';
import {
	FormattedMessage,
	defineMessages,
} from 'react-intl';

import type CK from '~/types/contentking';

import ResizableDatatable, {
	type ResizableDatatableColumnDefinitions,
	ResizableDatatableHeaderHeight,
} from '~/components/logic/datatables/ResizableDatatable';
import TextualCellValue from '~/components/logic/datatables/cellValues/TextualCellValue';
import UrlCellValue from '~/components/logic/datatables/cellValues/UrlCellValue';

import {
	useIncomingInternalLinksQuery,
} from './IncomingInternalLinksTable.gql';

import useGraphQLConnection from '~/hooks/useGraphQLConnection';
import useNavigate from '~/hooks/useNavigate';



const filter = Immutable.Map({});

const messages = defineMessages({
	columnLabel: {
		id: 'ui.pageDetail.linkData.outbound.columns.label',
	},
	columnTitle: {
		id: 'ui.pageDetail.linkData.outbound.columns.title',
	},
	columnUrl: {
		id: 'ui.pageDetail.linkData.outbound.columns.url',
	},
});



type Props = {
	fallbackLinksCount: number | undefined,
	legacyUrlId: number,
	websiteId: CK.WebsiteId,
};

const IncomingInternalLinksTable: React.FC<Props> = (props) => {
	const {
		fallbackLinksCount,
		legacyUrlId,
		websiteId,
	} = props;

	const navigate = useNavigate();

	const { data, fetchMore } = useIncomingInternalLinksQuery({
		variables: {
			legacyUrlId,
			limit: 100,
			offset: 0,
			websiteId,
		},
	});

	const graphQLConnection = useGraphQLConnection({
		batchLimit: 100,
		context: {
			websiteId,
		},
		data,
		fallbackTotalCount: fallbackLinksCount,
		fetchMore,
		getter: (data) => data.lookupPageByLegacyId?.incomingInternalLinks ?? null,
		pollIntervalInMilliseconds: 10_000,
	});

	const handleClick = React.useCallback(
		({ row }, event) => {
			if (row === null || row.sourceLegacyId === null) {
				return;
			}

			navigate({
				event,
				routeName: 'website.pages.detail',
				routeParams: {
					id: row.sourceLegacyId,
					websiteId,
				},
			});
		},
		[
			navigate,
			websiteId,
		],
	);

	const columns: ResizableDatatableColumnDefinitions<typeof graphQLConnection.rowGetter> = React.useMemo(
		() => {
			return [
				{
					name: 'incomingInternalLinksSourceUrl',
					render: {
						cell: ({ row }) => (
							<UrlCellValue value={row.sourceUrl} />
						),
						label: (
							<FormattedMessage {...messages.columnUrl} />
						),
					},
					resizable: true,
					width: 450,
				},
				{
					name: 'incomingInternalLinksAnchorText',
					render: {
						cell: ({ row }) => (
							<TextualCellValue value={row.anchorText} />
						),
						label: (
							<FormattedMessage {...messages.columnLabel} />
						),
					},
					resizable: true,
					width: 200,
				},
				{
					name: 'incomingInternalLinksAnchorTitle',
					render: {
						cell: ({ row }) => (
							<TextualCellValue value={row.anchorTitle} />
						),
						label: (
							<FormattedMessage {...messages.columnTitle} />
						),
					},
					resizable: true,
					width: 300,
				},
			];
		},
		[],
	);

	return (
		<ResizableDatatable
			blankSlate={null}
			columns={columns}
			filter={filter}
			headerHeight={ResizableDatatableHeaderHeight.Small}
			isLoading={graphQLConnection.isLoaded === false}
			onRowClick={handleClick}
			onScroll={graphQLConnection.fetchFurtherRange}
			rowGetter={graphQLConnection.rowGetter}
			rowsCount={graphQLConnection.totalCount}
		/>
	);
};



export default IncomingInternalLinksTable;
